/**
 * Payment Routing Hook
 *
 * PHASE 1.2: React Hook for Cross-Platform Payment Routing
 *
 * This hook provides a unified React interface for payment operations that
 * automatically routes to the appropriate payment system based on platform.
 *
 * FEATURES:
 * - Unified payment interface for all platforms
 * - Automatic platform detection and routing
 * - State management for payment processing
 * - Error handling and loading states
 * - Debug utilities for development
 *
 * USAGE:
 * - Use in subscription components instead of direct payment calls
 * - Provides consistent interface regardless of underlying payment system
 * - Handles platform-specific payment flows transparently
 *
 * INTEGRATION:
 * - Works with PaymentRoutingService for actual routing logic
 * - Maintains compatibility with existing subscription UI components
 * - Provides debugging information for development and testing
 */

import { useCallback, useState } from 'react';
import { paymentRoutingService, type PaymentRequest, type PaymentResult } from '@/services/payments';

export interface PaymentRoutingState {
  readonly isProcessing: boolean;
  readonly paymentMethod: 'ios_iap' | 'stripe';
  readonly error?: string;
}

export const usePaymentRouting = () => {
  const [state, setState] = useState<PaymentRoutingState>({
    isProcessing: false,
    paymentMethod: paymentRoutingService.getPaymentMethod()
  });

  /**
   * Initiate payment using the appropriate payment system
   */
  const initiatePayment = useCallback(async (
    priceType: 'monthly' | 'annual',
    referralCode?: string
  ): Promise<PaymentResult> => {
    console.log('🚀 Payment Routing Hook: Initiating payment:', { priceType, referralCode });

    setState(prev => ({
      ...prev,
      isProcessing: true,
      error: undefined
    }));

    try {
      const request: PaymentRequest = {
        priceType,
        referralCode
      };

      const result = await paymentRoutingService.initiatePayment(request);

      if (!result.success) {
        setState(prev => ({
          ...prev,
          isProcessing: false,
          error: result.error
        }));
      } else {
        // For Stripe, the redirect happens automatically
        // For iOS IAP, we need to handle the success differently
        if (result.method === 'ios_iap') {
          setState(prev => ({
            ...prev,
            isProcessing: false
          }));
        }
        // For Stripe, we don't reset the state since the page will redirect
      }

      return result;
    } catch (error) {
      console.error('❌ Payment Routing Hook: Payment failed:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Payment failed';
      
      setState(prev => ({
        ...prev,
        isProcessing: false,
        error: errorMessage
      }));

      return {
        success: false,
        method: paymentRoutingService.getPaymentMethod(),
        error: errorMessage
      };
    }
  }, []);

  /**
   * Check if iOS IAP is available
   */
  const checkIOSIAPAvailability = useCallback(async (): Promise<boolean> => {
    return await paymentRoutingService.isIOSIAPAvailable();
  }, []);

  /**
   * Get platform information for debugging
   */
  const getPlatformInfo = useCallback(() => {
    return paymentRoutingService.getPlatformInfo();
  }, []);

  /**
   * Reset payment state
   */
  const resetState = useCallback(() => {
    setState({
      isProcessing: false,
      paymentMethod: paymentRoutingService.getPaymentMethod()
    });
  }, []);

  return {
    // State
    ...state,
    
    // Actions
    initiatePayment,
    checkIOSIAPAvailability,
    getPlatformInfo,
    resetState
  };
};
